import { normalizeText } from '@tinct/core-dom';
import type { VariantLogo } from './logos.js';

/**
 * Placeholder the backend writes into every `<img>` src of a generated
 * `customer_logo` template value (`srcset`/`data-src`/`<source>` candidates
 * are stripped server-side, so a placeholder `src` marks each logo slot).
 */
export const LOGO_PLACEHOLDER = 'LOGO_URL';

/**
 * Attribute the backend stamps on every placeholder: the index of the distinct
 * logo it renders. Marquee clones of one logo share the index, so a template
 * declares fewer unique slots than it has placeholders.
 */
export const SLOT_INDEX_ATTR = 'data-tinct-slot';
/**
 * Grouped templates (one block spanning several logo rows, e.g. a desktop
 * marquee and the mobile marquee hidden at the desktop capture) wrap each
 * row's markup in an element carrying these: the row's order in the block's
 * selector list, and its distinct-logo count. `data-tinct-slot` continues
 * across rows, so row n's companies start at the sum of the earlier counts.
 */
export const CAROUSEL_ATTR = 'data-tinct-carousel';
export const CAROUSEL_SLOT_COUNT_ATTR = 'data-tinct-slot-count';

/** Marker attribute on the `<img>` the SDK inserts next to an svg visual. */
const INSERTED_ATTR = 'data-tinct-inserted';

/**
 * Attribute persisting a live visual's original identity: set the first time
 * the visual is seen, before its source is rewritten, so every later pass
 * (rAF loop, observer, host revert) maps it to the same slot. Copied along by
 * `cloneNode`, so clones a slider creates after the first pass inherit it.
 */
const LOGO_KEY_ATTR = 'data-tinct-logo-key';

/**
 * Source attributes that identify an `<img>`, lazy-load ones first (a loader
 * leaves the same 1×1 placeholder in every `src` while the real URL sits in
 * `data-src`). Mirrors the backend's `CustomerLogoSlots`, so the live DOM and
 * the captured markup agree on what a slot is.
 */
const SOURCE_ATTRS = [
  'data-src',
  'data-lazy-src',
  'data-original',
  'data-lazy',
  'data-srcset',
  'srcset',
  'src',
];

/** Keys longer than this (data-URI backgrounds, svg markup) are hashed before persisting. */
const MAX_KEY_LENGTH = 256;

/** dataset key holding the applied-logo signature of a mutated visual. */
const LOGO_SIGNATURE_KEY = 'tinctLogo';

/** Resolved heights within this range of each other count as one uniform row. */
const UNIFORM_HEIGHT_TOLERANCE_PX = 2;

/** How many ancestors may separate a logo visual from its cell. */
const MAX_CELL_DEPTH = 4;

/** Natural pixel dimensions of a loaded image. */
export interface NaturalDims {
  width: number;
  height: number;
}

/**
 * Resolves the natural dimensions of logo images the payload didn't carry
 * dimensions for, by loading each once — the browser caches the response, so
 * the actual render reuses it. A failed load (or an environment without
 * `Image`) resolves to `null` so application proceeds without width fitting
 * for that logo.
 */
export function loadNaturalDimensions(
  srcs: string[],
): Promise<Map<string, NaturalDims | null>> {
  if (typeof Image === 'undefined') {
    return Promise.resolve(new Map(srcs.map((src) => [src, null])));
  }
  return Promise.all(
    srcs.map(
      (src) =>
        new Promise<[string, NaturalDims | null]>((resolve) => {
          const img = new Image();
          img.onload = () =>
            resolve([
              src,
              img.naturalWidth && img.naturalHeight
                ? { width: img.naturalWidth, height: img.naturalHeight }
                : null,
            ]);
          img.onerror = () => resolve([src, null]);
          img.src = src;
        }),
    ),
  ).then((entries) => new Map(entries));
}

/**
 * Where a logo's ink sits inside its image file: the bounding box of its
 * non-background pixels as fractions of the natural size, plus that size.
 */
export interface InkBox {
  naturalWidth: number;
  naturalHeight: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * How much of its box a row's logo ink fills, per axis (fractions of the
 * box), measured on the originals (see `inkFractionFor`).
 */
export interface InkFraction {
  width: number;
  height: number;
}

/** Longest side of the downscaled sample an ink probe reads. */
const INK_SAMPLE_PX = 96;
/** Alpha below which a pixel is background. */
const INK_ALPHA_MIN = 16;
/** Summed RGB distance from the background colour above which a pixel is ink. */
const INK_COLOR_DISTANCE_MIN = 60;
/** Every channel at or above this: an opaque background counts as white (clear space). */
const INK_NEAR_WHITE_MIN = 235;

/**
 * Bounding box of the ink in an RGBA pixel buffer, as fractions of the
 * buffer's size. The background is transparency when the corners are
 * transparent, else the first opaque corner's colour (a wordmark on a white
 * or brand-coloured card); ink is any pixel that is not background. Null
 * when the image has no ink at all.
 */
export function inkBoundsOfPixels(
  data: ArrayLike<number>,
  width: number,
  height: number,
): { left: number; top: number; width: number; height: number } | null {
  const at = (x: number, y: number) => (y * width + x) * 4;
  const corners = [at(0, 0), at(width - 1, 0), at(0, height - 1), at(width - 1, height - 1)];
  const opaqueCorner = corners.find((i) => data[i + 3] >= INK_ALPHA_MIN);
  const transparentBackground = opaqueCorner === undefined;
  const bg = opaqueCorner === undefined ? [0, 0, 0] : [data[opaqueCorner], data[opaqueCorner + 1], data[opaqueCorner + 2]];
  // An opaque card that is not white IS the mark (white letters on a black
  // card): the whole file is ink. Measuring only the letters would scale the
  // card up over the neighbours.
  if (!transparentBackground && bg.some((channel) => channel < INK_NEAR_WHITE_MIN)) {
    return { left: 0, top: 0, width: 1, height: 1 };
  }

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = at(x, y);
      const a = data[i + 3];
      if (a < INK_ALPHA_MIN) continue;
      if (!transparentBackground) {
        const distance =
          Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2]);
        if (distance < INK_COLOR_DISTANCE_MIN) continue;
      }
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return {
    left: minX / width,
    top: minY / height,
    width: (maxX - minX + 1) / width,
    height: (maxY - minY + 1) / height,
  };
}

/**
 * Measures where the ink sits in each image (see `InkBox`) by drawing a
 * small copy of it on a canvas. Cross-origin images need CORS (the request
 * is anonymous); a tainted canvas, a failed load or an environment without
 * canvas resolves to null for that source, and the caller keeps today's
 * sizing.
 */
export function loadInkBoxes(srcs: string[]): Promise<Map<string, InkBox | null>> {
  if (typeof Image === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(new Map(srcs.map((src) => [src, null])));
  }
  return Promise.all(
    srcs.map(
      (src) =>
        new Promise<[string, InkBox | null]>((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            try {
              const nw = img.naturalWidth;
              const nh = img.naturalHeight;
              if (!nw || !nh) return resolve([src, null]);
              const scale = Math.min(1, INK_SAMPLE_PX / Math.max(nw, nh));
              const w = Math.max(1, Math.round(nw * scale));
              const h = Math.max(1, Math.round(nh * scale));
              const canvas = document.createElement('canvas');
              canvas.width = w;
              canvas.height = h;
              const ctx = canvas.getContext('2d', { willReadFrequently: true });
              if (!ctx) return resolve([src, null]);
              ctx.drawImage(img, 0, 0, w, h);
              const bounds = inkBoundsOfPixels(ctx.getImageData(0, 0, w, h).data, w, h);
              resolve([src, bounds ? { naturalWidth: nw, naturalHeight: nh, ...bounds } : null]);
            } catch {
              resolve([src, null]);
            }
          };
          img.onerror = () => resolve([src, null]);
          img.src = src;
        }),
    ),
  ).then((entries) => new Map(entries));
}

// Ink boxes by ORIGINAL source: probed once per page, kept for every later
// pass (the originals are gone from the DOM after the first swap).
const inkBoxes = new Map<string, InkBox | null>();
const inkRequested = new Set<string>();
/** Bound for the per-page ink caches: a long-lived tab cycling through many logo sets starts over. */
const INK_CACHE_MAX = 2000;

/** Seeds the ink cache (tests, or a host that measured the originals itself). */
export function primeInkBox(src: string, box: InkBox | null): void {
  inkBoxes.set(src, box);
  inkRequested.add(src);
}

function requestInkBoxes(srcs: string[], onResolved?: () => void): void {
  if (inkBoxes.size > INK_CACHE_MAX) {
    inkBoxes.clear();
    inkRequested.clear();
  }
  const pending = srcs.filter((src) => !inkRequested.has(src));
  if (pending.length === 0) return;
  pending.forEach((src) => inkRequested.add(src));
  loadInkBoxes(pending).then((loaded) => {
    loaded.forEach((box, src) => inkBoxes.set(src, box));
    onResolved?.();
  });
}

/** An original visual: its source and the box the design rendered it in. */
export interface OriginalVisual {
  src: string;
  box: { width: number; height: number };
}

/**
 * The share of its box a row's logo ink fills — each original contained in
 * its own box, its ink measured, the row's median taken per axis (one odd
 * asset does not set the size). Null until at least half of the originals
 * are measured. A design's assets carry whitespace (a wordmark file with
 * its clear space baked in); Tinct's are cropped to the ink, so an injected
 * logo must be fitted to this fraction of whatever box it gets, or it
 * renders up to twice as large as the original did and crowds the row.
 */
export function inkFractionFor(
  originals: { ink: InkBox | null | undefined; box: { width: number; height: number } }[],
): InkFraction | null {
  const measured = originals.filter(
    (o): o is { ink: InkBox; box: { width: number; height: number } } =>
      !!o.ink && o.box.width > 0 && o.box.height > 0,
  );
  if (measured.length === 0 || measured.length * 2 < originals.length) return null;
  const fractions = measured.map(({ ink, box }) => {
    const scale = Math.min(box.width / ink.naturalWidth, box.height / ink.naturalHeight);
    return {
      width: Math.min(1, (ink.naturalWidth * ink.width * scale) / box.width),
      height: Math.min(1, (ink.naturalHeight * ink.height * scale) / box.height),
    };
  });
  return {
    width: medianOf(fractions.map((f) => f.width)),
    height: medianOf(fractions.map((f) => f.height)),
  };
}

/** Ink position inside a file, as fractions of the file (a whole file when unknown). */
type InkArea = { left: number; top: number; width: number; height: number };
const WHOLE_FILE: InkArea = { left: 0, top: 0, width: 1, height: 1 };
/** Sanity bounds for the scale placing a logo's ink (a bad asset never explodes). */
const INK_SCALE_MIN = 0.05;
const INK_SCALE_MAX = 4;

/**
 * How to draw a logo file inside its box so that its INK — not its file —
 * takes the share of the box the originals' ink took, centred. The file is
 * first contained in the box (`object-fit: contain` does that), then scaled
 * and shifted about the box centre: a file cropped tight scales down, a
 * file with wide margins around a small mark scales up and re-centres on
 * the mark. Tinct's assets are not consistent in that respect, and a design
 * measures logos by their ink.
 */
export function placeInk(
  ratio: number,
  box: { width: number; height: number },
  fraction: InkFraction,
  ink: InkArea = WHOLE_FILE,
): { scale: number; tx: number; ty: number; fileWidth: number; fileHeight: number } {
  const fileHeight = Math.min(box.height, box.width / ratio);
  const fileWidth = fileHeight * ratio;
  const inkWidth = fileWidth * ink.width;
  const inkHeight = fileHeight * ink.height;
  const target = fitRatioIn(inkWidth / inkHeight, {
    width: box.width * fraction.width,
    height: box.height * fraction.height,
  });
  const scale = Math.min(INK_SCALE_MAX, Math.max(INK_SCALE_MIN, target.height / inkHeight));
  // Ink centre relative to the box centre, before scaling.
  const cx = (box.width - fileWidth) / 2 + (ink.left + ink.width / 2) * fileWidth - box.width / 2;
  const cy = (box.height - fileHeight) / 2 + (ink.top + ink.height / 2) * fileHeight - box.height / 2;
  return { scale, tx: -cx * scale || 0, ty: -cy * scale || 0, fileWidth, fileHeight };
}

/** Largest box of the given ratio that fits inside `target`. */
function fitRatioIn(
  ratio: number,
  target: { width: number; height: number },
): { width: number; height: number } {
  const height = Math.min(target.height, target.width / ratio);
  return { width: height * ratio, height };
}

/** The original source a live visual shows, when it can be probed. */
function originalSourceOf(visual: Element): string | null {
  if (isImg(visual)) {
    const src = visual.currentSrc || visual.getAttribute('src');
    return isUsableSource(src) ? src : null;
  }
  const bg = computedStyleOf(visual)?.backgroundImage ?? '';
  const url = bg.match(/url\((['"]?)(.*?)\1\)/)?.[2];
  return url && isUsableSource(url) ? url : null;
}

// The originals of each row, read on the first pass (before any swap) and
// kept for the passes that follow.
const rowOriginals = new WeakMap<Element, OriginalVisual[]>();

/** A positive pixel count parsed from a CSS length, or null. */
function pxValue(value: string | null | undefined): number | null {
  if (!value || !value.endsWith('px')) return null;
  const n = parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Realm-safe element helpers: the editor previewer applies templates to
// elements inside an iframe, whose element constructors differ from the
// app's — `instanceof HTMLImageElement` is false there and would misroute an
// img into the background-image branch. Tag/shape checks and the element's
// own window work in every realm (including the SDK's same-realm case).

function isImg(el: Element): el is HTMLImageElement {
  return el.tagName.toLowerCase() === 'img';
}

/** The element's inline style declaration, when it has one. */
function styleOf(el: Element): CSSStyleDeclaration | null {
  return (el as HTMLElement).style ?? null;
}

/** Computed style from the element's own realm, or null off-DOM. */
function computedStyleOf(el: Element): CSSStyleDeclaration | null {
  const view = el.ownerDocument?.defaultView;
  return view ? view.getComputedStyle(el) : null;
}

/**
 * The parsed shape of a `customer_logo` template value. The template is never
 * injected: it only GUIDES the in-place mutation of the live DOM — how many
 * logo slots the captured design had, and any per-slot declared heights.
 */
export interface LogoTemplate {
  /** Placeholder count — one per rendered `<img>`, marquee clones included. */
  slotCount: number;
  /**
   * Distinct-logo index of each placeholder (from `data-tinct-slot`); identity
   * `[0..slotCount-1]` for templates captured before the stamp existed.
   */
  slotIndices: number[];
  /** Distinct logos the captured strip rendered — the number of companies it can show. */
  uniqueSlotCount: number;
  /** Per-slot declared height from the captured markup, when any. */
  slotHeights: (number | null)[];
  /** Whether the capture annotated logo cells with `data-tinct-removable`. */
  annotated: boolean;
  /**
   * The rows the template spans, in selector-list order: where each row's
   * companies start in the block's single ordered list, and how many it
   * shows. A single-row template has one entry at offset 0 (see
   * `logosForCarousel`).
   */
  carousels: LogoCarousel[];
}

export interface LogoCarousel {
  /** Index of the row's first company in the block's list. */
  offset: number;
  /** Distinct logos the row shows. */
  slotCount: number;
  /**
   * Distinct-logo index of each of the row's OWN placeholders, numbered
   * from 0 within the row (the template stamps continue across rows).
   */
  slotIndices: number[];
}

/**
 * The companies of the n-th element a grouped block's selector matched —
 * the rows come in the selector list's order, which the backend aligns with
 * the wrappers'. Extra matches beyond the declared rows (copies of a row
 * the page repeats) get the first row's companies, as before grouping.
 */
export function logosForCarousel<T>(template: LogoTemplate, logos: T[], index: number): T[] {
  const carousel = template.carousels[index] ?? template.carousels[0];
  if (!carousel) return logos;
  return logos.slice(carousel.offset, carousel.offset + carousel.slotCount);
}

/**
 * Declared height of a template slot: its own inline px `height` style, its
 * `height` attribute, or the nearest wrapper's inline px height — walking up
 * to the slot's annotated cell (or, un-annotated, while the wrapper chain
 * stays an only-child chain, depth-capped).
 */
function slotDeclaredHeight(img: HTMLImageElement): number | null {
  const own =
    pxValue(img.style.height) ??
    (Number(img.getAttribute('height')) > 0 ? Number(img.getAttribute('height')) : null);
  if (own) return own;

  const boundary = img.closest('[data-tinct-removable]');
  let el: HTMLElement | null = img.parentElement;
  for (let depth = 0; el && depth < MAX_CELL_DEPTH; depth++) {
    const h = pxValue(el.style.height);
    if (h) return h;
    const isCell = boundary
      ? el === boundary
      : (el.parentElement?.children.length ?? 1) > 1;
    if (isCell) break;
    el = el.parentElement;
  }
  return null;
}

/**
 * Parses a template value into its guide shape. Returns null when the value
 * carries no placeholder slot — inline-SVG carousels (`logoCount` 0) — so the
 * caller falls back to injecting the value as-is.
 */
export function parseLogoTemplate(value: string | null | undefined): LogoTemplate | null {
  if (!value?.includes(LOGO_PLACEHOLDER)) return null;

  const doc = new DOMParser().parseFromString(value, 'text/html');
  const slots = Array.from(doc.querySelectorAll('img')).filter(
    (img) => img.getAttribute('src') === LOGO_PLACEHOLDER,
  );
  if (slots.length === 0) return null;

  const stamped = slots.map((img) => img.getAttribute(SLOT_INDEX_ATTR));
  const slotIndices = stamped.every((raw) => raw !== null && /^\d+$/.test(raw))
    ? stamped.map(Number)
    : slots.map((_img, i) => i);

  const uniqueSlotCount = new Set(slotIndices).size;
  return {
    slotCount: slots.length,
    slotIndices,
    uniqueSlotCount,
    slotHeights: slots.map(slotDeclaredHeight),
    annotated: doc.querySelector('[data-tinct-removable]') !== null,
    carousels: parseCarousels(doc, slots, slotIndices, uniqueSlotCount),
  };
}

/**
 * The rows of a grouped template (outermost `[data-tinct-carousel]`
 * wrappers, document order): each row's distinct count from its stamp,
 * else from the distinct slots among its own placeholders; offsets
 * accumulate. A template without wrappers is one row.
 */
function parseCarousels(
  doc: Document,
  slots: HTMLImageElement[],
  slotIndices: number[],
  uniqueSlotCount: number,
): LogoCarousel[] {
  const wrappers = Array.from(doc.querySelectorAll(`[${CAROUSEL_ATTR}]`)).filter(
    (el) => !el.parentElement?.closest(`[${CAROUSEL_ATTR}]`),
  );
  if (wrappers.length === 0) return [{ offset: 0, slotCount: uniqueSlotCount, slotIndices }];

  let offset = 0;
  return wrappers.map((wrapper) => {
    const stamped = Number(wrapper.getAttribute(CAROUSEL_SLOT_COUNT_ATTR));
    const own = slots.flatMap((img, i) => (wrapper.contains(img) ? [slotIndices[i]] : []));
    const slotCount = Number.isInteger(stamped) && stamped > 0 ? stamped : new Set(own).size;
    const carousel = { offset, slotCount, slotIndices: own.map((index) => index - offset) };
    offset += slotCount;
    return carousel;
  });
}

/** A real source: not blank, not `about:blank`, not a lazy-loader's inline gif. */
function isUsableSource(value: string | null): value is string {
  if (!value || !value.trim()) return false;
  const lower = value.trim().toLowerCase();
  return lower !== 'about:blank' && !lower.startsWith('data:image/gif');
}

/** Bounds a key's length with a small string hash (no crypto needed: only equality matters). */
function boundedKey(key: string): string {
  if (key.length <= MAX_KEY_LENGTH) return key;
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (Math.imul(31, hash) + key.charCodeAt(i)) | 0;
  }
  return `#${key.length}:${(hash >>> 0).toString(36)}`;
}

/**
 * Identity of a live visual's ORIGINAL logo, persisted on first sight. An
 * `<img>` is identified by its first usable source (whole `srcset` string —
 * URLs may hold commas, splitting on them would merge distinct logos), an
 * inline `<svg>` by its markup (slider clones are byte-identical), a
 * background-image element by its background. Null when nothing identifies
 * the visual — it is then a slot of its own.
 */
export function liveIdentityKey(visual: Element): string | null {
  const persisted = visual.getAttribute(LOGO_KEY_ATTR);
  if (persisted) return persisted;

  let key: string | null = null;
  const tag = visual.tagName.toLowerCase();
  if (tag === 'img') {
    for (const attr of SOURCE_ATTRS) {
      const value = visual.getAttribute(attr);
      if (isUsableSource(value)) {
        key = `${attr}=${normalizeText(value)}`;
        break;
      }
    }
  } else if (tag === 'svg') {
    key = `svg=${normalizeText(visual.outerHTML)}`;
  } else {
    const inline = styleOf(visual)?.backgroundImage;
    const bg = inline && inline.includes('url(') ? inline : computedStyleOf(visual)?.backgroundImage;
    if (bg && bg.includes('url(')) key = `bg=${normalizeText(bg)}`;
  }
  if (key === null) return null;

  const bounded = boundedKey(key);
  visual.setAttribute(LOGO_KEY_ATTR, bounded);
  return bounded;
}

/** Slot assignment of the live visuals: which distinct logo each one renders. */
export interface SlotAssignment {
  /** Slot index per visual, parallel to the visuals. */
  slotOf: number[];
  /** Distinct slots among the visuals. */
  uniqueCount: number;
}

/**
 * Numbers the live visuals by identity in first-appearance order, so every
 * clone of a logo shares a slot — whatever the clone count, which sliders
 * vary with the viewport and the SDK may see before or after they mount.
 * Only when NO visual carries an identity (sources rewritten to opaque blobs)
 * and the live count is a whole number of captured strips does the template's
 * own numbering stand in.
 */
export function assignSlots(
  visuals: Element[],
  template: LogoTemplate,
  /** The row being applied (grouped templates): its own placeholder numbering. */
  carousel: LogoCarousel = template.carousels[0],
): SlotAssignment {
  const keys = visuals.map(liveIdentityKey);
  const indices = carousel?.slotIndices ?? template.slotIndices;
  if (keys.every((key) => key === null) && indices.length > 0 && visuals.length % indices.length === 0) {
    const slotOf = visuals.map((_v, i) => indices[i % indices.length]);
    return { slotOf, uniqueCount: new Set(slotOf).size };
  }

  const byKey = new Map<string, number>();
  let next = 0;
  const slotOf = keys.map((key) => {
    if (key === null) return next++;
    const known = byKey.get(key);
    if (known !== undefined) return known;
    byKey.set(key, next);
    return next++;
  });
  return { slotOf, uniqueCount: next };
}

/**
 * True when the element renders a logo visual on the live page: an `<img>`,
 * an inline `<svg>`, or a plain element with a CSS `background-image`. Images
 * the SDK inserted itself (svg replacements) are not source visuals.
 */
function isLogoVisual(el: Element): boolean {
  if (el.hasAttribute(INSERTED_ATTR)) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'img' || tag === 'svg') return true;
  const bg = computedStyleOf(el)?.backgroundImage;
  return !!bg && bg !== 'none' && bg.includes('url(');
}

/**
 * The live page's original logo visuals inside the target element, deepest
 * match only (a background-image wrapper around an `<img>` is one logo, not
 * two), in document order — paralleling the captured template's slot order.
 */
export function findLogoVisuals(root: Element): Element[] {
  const candidates = Array.from(root.querySelectorAll('*')).filter(isLogoVisual);
  return candidates.filter(
    (el) => !candidates.some((other) => other !== el && el.contains(other)),
  );
}

/**
 * The live cell owning a visual: the nearest ancestor that has element
 * siblings (the row's repeating unit — e.g. the `li` of a carousel), found by
 * climbing the visual's only-child wrapper chain, depth-capped, never
 * reaching the target element itself. Falls back to the visual.
 */
export function findVisualCell(visual: Element, root: Element): Element {
  let cell: Element = visual;
  for (let depth = 0; depth < MAX_CELL_DEPTH; depth++) {
    const parent = cell.parentElement;
    if (!parent || cell === root || parent === root) break;
    if (parent.children.length > 1) break;
    cell = parent;
  }
  return cell;
}

/**
 * Declared box of a live visual: inline px styles, `width`/`height`
 * attributes (imgs), or px-resolving computed styles — checked on the visual
 * first, then up its wrapper chain to the cell (inclusive), taking the first
 * element where BOTH dimensions resolve (the box ratio is only meaningful
 * when both come from the same box). Declared/computed values are
 * transform-immune, so appear animations can't distort them.
 */
function declaredVisualSize(
  visual: Element,
  cell: Element,
): { width: number; height: number } | null {
  let el: Element | null = visual;
  while (el) {
    let w: number | null = null;
    let h: number | null = null;
    const style = styleOf(el);
    if (style) {
      w = pxValue(style.width);
      h = pxValue(style.height);
    }
    if (!w || !h) {
      const cs = computedStyleOf(el);
      if (cs) {
        w = w ?? pxValue(cs.width);
        h = h ?? pxValue(cs.height);
      }
    }
    // Attributes last: they are intrinsic-size hints and can lie about the
    // rendered box when the page's CSS rescales the img.
    if (isImg(el)) {
      w = w ?? (Number(el.getAttribute('width')) > 0 ? Number(el.getAttribute('width')) : null);
      h = h ?? (Number(el.getAttribute('height')) > 0 ? Number(el.getAttribute('height')) : null);
    }
    if (w && h) return { width: w, height: h };
    if (el === cell) break;
    el = el.parentElement;
  }
  return null;
}

/** Persisted per visual: the box the design gave it, read before any swap. */
const OWN_BOX_ATTR = 'data-tinct-logo-box';

/**
 * The box the design rendered this visual in — read once, before the first
 * swap, and persisted on the visual: after a swap the computed box may
 * follow the injected file (a `width: auto` img), and after our own sizing
 * it is ours, not the design's.
 */
function ownBoxOf(visual: Element, cell: Element): { width: number; height: number } | null {
  const stored = visual.getAttribute(OWN_BOX_ATTR);
  if (stored) {
    const [w, h] = stored.split('x').map(Number);
    if (w > 0 && h > 0) return { width: w, height: h };
  }
  const size = declaredVisualSize(visual, cell);
  if (size) visual.setAttribute(OWN_BOX_ATTR, `${size.width}x${size.height}`);
  return size;
}

/**
 * One point of the original row's sizing curve: the designer gave a logo of
 * this aspect ratio a box of this width × height.
 */
export interface CalibrationPair {
  ratio: number;
  height: number;
  width?: number;
}

/**
 * Learns the original row's shape→height mapping from the live visuals'
 * declared boxes. Logo walls normalize optically: wide wordmarks sit in a
 * low "text-height" band while square marks get roughly double the height so
 * every logo carries the same visual weight — and each original box's
 * (ratio, height) is one hand-tuned sample of that curve.
 */
export function rowCalibration(visuals: Element[], cells: Element[]): CalibrationPair[] {
  return visuals
    .map((visual, i): CalibrationPair | null => {
      const size = declaredVisualSize(visual, cells[i]);
      return size
        ? { ratio: size.width / size.height, height: size.height, width: size.width }
        : null;
    })
    .filter((pair): pair is CalibrationPair => pair !== null && pair.ratio > 0);
}

/**
 * True when the original row places every logo in one shared box. That design
 * normalizes by CONTAINMENT — each asset, whatever its ratio, is fitted
 * inside the common box (`object-fit`/`background-size: contain`) — so an
 * injected logo must be fitted the same way: keep the boxes untouched and let
 * contain do the work. Sizing boxes to the injected logo's ratio here (the
 * varied-box strategy) would blow tight wordmarks up to the box height and
 * read oversized.
 *
 * Majority-based on purpose: a modal box shared by ≥60% of the samples wins,
 * so one visual caught mid-load (transient auto/zero box) can't flip a
 * uniform row into the varied-box strategy.
 *
 * A dissenter sharing the modal WIDTH but not its height disqualifies the
 * regime entirely: that is the signature of a width-driven design
 * (`width: 100%` + auto height), where every box's height merely mirrors its
 * ORIGINAL asset's ratio — there is no fixed box to contain in, and an
 * auto-height img would render the injected file at ITS natural ratio
 * instead. Those rows must take the per-ratio height strategies.
 */
export function modalRowBox(
  pairs: CalibrationPair[],
): { width: number; height: number } | null {
  const boxes = pairs.filter((p) => p.width);
  if (boxes.length < 2) return null;
  const counts = new Map<string, number>();
  for (const p of boxes) {
    const key = `${Math.round(p.width as number)}x${Math.round(p.height)}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const [modalKey, modalCount] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
  if (modalCount < 2 || modalCount / boxes.length < 0.6) return null;
  const [w, h] = modalKey.split('x').map(Number);
  // Width-driven means a fair share of the heights follow their asset: one
  // taller cell in a long uniform row (a `tb-tall` exception, a featured
  // logo) is a design choice, not a different regime.
  const dissenters = boxes.filter(
    (p) =>
      Math.abs((p.width as number) - w) <= UNIFORM_HEIGHT_TOLERANCE_PX &&
      Math.abs(p.height - h) > UNIFORM_HEIGHT_TOLERANCE_PX,
  ).length;
  const widthDriven = dissenters * 4 >= boxes.length;
  if (widthDriven) return null;
  return { width: w, height: h };
}

/** Sanity bounds for a derived logo height, used off-curve. */
const MIN_DERIVED_HEIGHT_PX = 8;
const MAX_DERIVED_HEIGHT_PX = 96;

function medianOf(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

/**
 * The height the original designer would have given a logo of `ratio`,
 * derived from the row's calibration curve. A slot's height belongs to the
 * ORIGINAL logo's shape, not to its position — inheriting it puts a wide
 * wordmark in a square badge's tall box (and vice versa) and destroys the
 * row's optical balance, so the injected logo's own ratio drives its height:
 * - uniform curve (all heights within tolerance) → that fixed height;
 * - 3+ samples → linear interpolation over the sorted (ratio, height)
 *   samples, clamped to the curve's endpoints — reusing the designer's own
 *   hand-tuning;
 * - 1-2 samples → constant-visual-weight estimate `√(A_ref / ratio)` around
 *   the samples' median box area, sanity-clamped.
 */
export function heightForRatio(pairs: CalibrationPair[], ratio: number): number | null {
  if (pairs.length === 0 || ratio <= 0) return null;

  // A single sample says nothing about uniformity — a lone square badge must
  // not flatten every injected wordmark to its 50px.
  const heights = pairs.map((p) => p.height);
  if (
    pairs.length >= 2 &&
    Math.max(...heights) - Math.min(...heights) <= UNIFORM_HEIGHT_TOLERANCE_PX
  ) {
    return medianOf(heights);
  }

  if (pairs.length < 3) {
    const areaRef = medianOf(pairs.map((p) => p.height * p.height * p.ratio));
    return Math.min(
      MAX_DERIVED_HEIGHT_PX,
      Math.max(MIN_DERIVED_HEIGHT_PX, Math.sqrt(areaRef / ratio)),
    );
  }

  const sorted = [...pairs].sort((a, b) => a.ratio - b.ratio);
  if (ratio <= sorted[0].ratio) return sorted[0].height;
  const last = sorted[sorted.length - 1];
  if (ratio >= last.ratio) return last.height;
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (ratio >= a.ratio && ratio <= b.ratio) {
      const t = b.ratio === a.ratio ? 0 : (ratio - a.ratio) / (b.ratio - a.ratio);
      return a.height + t * (b.height - a.height);
    }
  }
  return last.height;
}

/** Fallback height when the injected logo's own ratio is unknown. */
function fallbackHeight(
  pairs: CalibrationPair[],
  template: LogoTemplate,
): number | null {
  if (pairs.length > 0) return medianOf(pairs.map((p) => p.height));
  const declared = template.slotHeights.filter((h): h is number => h !== null && h > 0);
  return declared.length > 0 ? medianOf(declared) : null;
}

/** Deterministic filter-element id for a fill color. */
function fillFilterId(fillColor: string): string {
  return `tinct-logo-fill-${fillColor.replace(/[^a-zA-Z0-9]/g, '')}`;
}

/**
 * Ensures the hidden inline SVG filter for `fillColor` exists in the document
 * (appended once to `<body>`; blocks sharing a color share the definition)
 * and returns its id. The filter replicates the editor's silhouette recolor:
 * transparent and near-white pixels stay background, everything else is
 * flooded with `fillColor`.
 */
export function ensureFillFilter(doc: Document, fillColor: string): string {
  const id = fillFilterId(fillColor);
  if (!doc.getElementById(id) && doc.body) {
    doc.body.insertAdjacentHTML(
      'beforeend',
      `<svg aria-hidden="true" style="position:absolute;width:0;height:0">` +
        `<filter id="${id}" color-interpolation-filters="sRGB">` +
        `<feColorMatrix in="SourceGraphic" type="luminanceToAlpha" result="lum"/>` +
        `<feComponentTransfer in="lum" result="notWhite">` +
        `<feFuncA type="linear" slope="-20" intercept="19"/>` +
        `</feComponentTransfer>` +
        `<feComposite in="notWhite" in2="SourceAlpha" operator="in" result="mask"/>` +
        `<feFlood flood-color="${fillColor}"/>` +
        `<feComposite in2="mask" operator="in"/>` +
        `</filter></svg>`,
    );
  }
  return id;
}

/** Outcome of one in-place application, for the debug event. */
export interface LogoTemplateApplyResult {
  /** Live visuals found inside the target element. */
  liveVisuals: number;
  /** Distinct logos among the live visuals (clones of one logo count once). */
  liveUniqueVisuals: number;
  /**
   * How visuals mapped to slots: `one-to-one` (no clones, as many visuals as
   * the template has distinct logos), `clones` (several visuals share a
   * slot), `mismatch` (distinct live logos differ from the template's — the
   * mapping still applies, by live identity).
   */
  pairing:
    | 'one-to-one'
    | 'clones'
    | 'mismatch'
    | 'no-visuals'
    | 'empty'
    /** Stylesheets still loading: nothing measured or touched, try again on `onRefine`. */
    | 'pending-styles'
    /** The row's original images still loading (a `width: auto` img has no box yet): same. */
    | 'pending-images';
  /** How injected heights were derived from the row's calibration curve. */
  strategy:
    | 'contain-box'
    | 'uniform'
    | 'interpolated'
    | 'constant-weight'
    | 'fallback'
    | 'none';
  /** The learned (ratio, height) samples of the original row. */
  calibration: CalibrationPair[];
  /** Applied heights per distinct slot, in slot order. */
  heights: (number | null)[];
  /** Visuals that received a logo. */
  applied: number;
  /** Cells hidden because no company was left for their slot. */
  hiddenCells: number;
  /**
   * Whether this pass changed any box or hid any cell — i.e. the row's
   * layout differs from what a slider measured at mount (see
   * `notifyLayoutChange`).
   */
  layoutChanged: boolean;
  /**
   * The share of its box the originals' ink filled, which the injected
   * logos are fitted to inside their own boxes (see `inkFractionFor`). Null
   * while the originals are being measured, or when they could not be.
   */
  inkFraction: InkFraction | null;
}

/**
 * Hides an element in place, idempotently (never deletes live nodes).
 * Returns whether it changed anything.
 */
function hideInPlace(el: Element): boolean {
  const style = (el as HTMLElement | SVGElement).style;
  if (!style || style.display === 'none') return false;
  style.display = 'none';
  return true;
}

/** Marks a cell hidden by `hideCell`, holding the inline display it had. */
const HIDDEN_CELL_ATTR = 'data-tinct-hidden-cell';

/**
 * Hides the cell of a slot that has no company, remembering that WE hid it
 * (and the inline display it had) so a later pass can show it again: the
 * selection can grow between passes over the same live nodes — the studio
 * adds buyers one by one — and a cell hidden for the first pass must come
 * back the moment a logo lands in it.
 */
function hideCell(cell: Element): boolean {
  const style = (cell as HTMLElement).style;
  if (!style) return false;
  // Remember the prior display once; a host script that re-showed the cell
  // since (display reverted) gets it hidden again on this pass.
  if (!cell.hasAttribute(HIDDEN_CELL_ATTR)) cell.setAttribute(HIDDEN_CELL_ATTR, style.display);
  if (style.display === 'none') return false;
  style.display = 'none';
  return true;
}

/** Undoes `hideCell` (only a cell we hid), restoring its prior inline display. */
function showCell(cell: Element): boolean {
  const prior = cell.getAttribute(HIDDEN_CELL_ATTR);
  if (prior === null) return false;
  cell.removeAttribute(HIDDEN_CELL_ATTR);
  const style = (cell as HTMLElement).style;
  if (!style || style.display === prior) return false;
  style.display = prior;
  return true;
}

/**
 * Windows with a slider re-measure already scheduled by
 * `notifyLayoutChange`: one event per window per pass, however many rows
 * the pass touched.
 */
const pendingLayoutNotifications = new WeakSet<Window>();

/**
 * Tells the page's sliders the row's geometry changed. Marquee libraries
 * (Splide, Swiper, Slick…) measure their slides at mount and loop over that
 * length; the logos injected here have their own ratios, so the sized cells
 * add up to a different track length than the originals — modjo.ai's 24
 * logos went from 3743px to 2770px, and the slider's loop kept running the
 * original ~970px past the real content: a blank stretch on every cycle.
 * The libraries all re-measure on the window's `resize` event, so that is
 * the signal — dispatched once per window, after the pass and after layout,
 * and only when a pass actually changed a box (a re-apply changes nothing,
 * so it never fires: no feedback with the SDK's own resize handling, which
 * ignores an unchanged width anyway).
 */
function notifyLayoutChange(element: Element): void {
  const win = element.ownerDocument.defaultView;
  if (!win || pendingLayoutNotifications.has(win)) return;
  pendingLayoutNotifications.add(win);
  setTimeout(() => {
    pendingLayoutNotifications.delete(win);
    win.dispatchEvent(new win.Event('resize'));
  }, 0);
}

/**
 * Applies the fitted box as inline styles from `from` up to `cell`
 * (inclusive): the cell chain's boxes were sized for the original logo and
 * would otherwise crop or letterbox the injected one. Inline styles beat the
 * site's CSS. The chain holds nothing but this logo (see findVisualCell), so
 * resizing it is safe.
 *
 * Absolutely positioned chain elements additionally get pinned to 0,0: their
 * stylesheet offsets bake centering constants for the ORIGINAL box (e.g.
 * Framer's `left: calc(49.3% - 27.25px)` where 27.25 is half the original
 * width), so once the chain is resized those constants shift the box inside
 * its — often overflow-hidden — parent and crop the logo. Every chain
 * element now shares one box, so 0,0 is exact alignment. The cell itself is
 * never pinned: its position in the row belongs to the page.
 */
/**
 * Writes the fitted box on the resolved chain (see `resolveChain`): every
 * element gets the size; positioned wrappers are pinned to 0,0 (their
 * stylesheet offsets bake centering constants for the ORIGINAL box).
 */
function sizeChain(chain: Element[], cell: Element, height: number, width: number): void {
  for (const node of chain) {
    const style = styleOf(node);
    if (!style) continue;
    style.height = `${height}px`;
    style.width = `${width}px`;
    if (node !== cell && computedStyleOf(node)?.position === 'absolute') {
      style.top = '0px';
      style.left = '0px';
      style.right = 'auto';
      style.bottom = 'auto';
    }
  }
}

/**
 * The elements a fitted box is written on, from the visual up: each
 * ancestor whose box is the child's box — a wrapper that exists for this
 * logo (Framer's `inset: 0` layer, an only-child span). An ancestor that is
 * bigger is sized by the layout, not by the logo (a `h-16` flex cell in a
 * `1fr` grid): writing a box on it breaks the grid, so the chain stops
 * below it. Read-only: resolved for every visual BEFORE the pass writes
 * anything, since a resized child no longer hugs its parent — and since
 * every read after a write forces the whole document to lay out again
 * (seconds, on a long page).
 */
function resolveChain(visual: Element, cell: Element): Element[] {
  const chain: Element[] = [];
  let el: Element | null = visual;
  while (el) {
    chain.push(el);
    if (el === cell) break;
    const parent: Element | null = el.parentElement;
    if (!parent || !hugs(parent, el)) break;
    el = parent;
  }
  return chain;
}

/** Whether `parent`'s box is (within a pixel) `child`'s box. */
function hugs(parent: Element, child: Element): boolean {
  const p = parent.getBoundingClientRect();
  const c = child.getBoundingClientRect();
  return Math.abs(p.width - c.width) <= 1 && Math.abs(p.height - c.height) <= 1;
}

/** The element's layout box (transforms excluded), or `fallback` when it has none yet. */
function layoutBoxOf(
  el: Element,
  fallback: { width: number; height: number },
): { width: number; height: number } {
  const { offsetWidth, offsetHeight } = el as HTMLElement;
  return offsetWidth > 0 && offsetHeight > 0
    ? { width: offsetWidth, height: offsetHeight }
    : fallback;
}

// Links already carrying our "styles arrived" listener.
const watchedLinks = new WeakSet<Element>();

/**
 * Stylesheet links of a still-loading document that have not applied yet.
 * Until they have, computed sizes come from attributes and defaults — a
 * calibration learned then (and persisted) would be wrong for good: a
 * Tailwind grid measured its imgs at their `width`/`height` attributes
 * (232×78) instead of the styled `max-h-[26px]` boxes.
 */
function pendingStylesheets(doc: Document): HTMLLinkElement[] {
  if (doc.readyState === 'complete') return [];
  return Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel~="stylesheet"][href]')).filter(
    (link) => !link.sheet,
  );
}

/**
 * The `<img>` standing in for an svg visual: svgs cannot display an external
 * raster, so the svg is hidden and a sibling img (the one additive DOM
 * insertion this module allows itself) carries the logo. Reused across
 * re-applies.
 */
function insertedImgFor(svg: Element): HTMLImageElement {
  const next = svg.nextElementSibling;
  if (next && isImg(next) && next.hasAttribute(INSERTED_ATTR)) {
    return next;
  }
  const img = svg.ownerDocument.createElement('img');
  img.setAttribute(INSERTED_ATTR, '');
  svg.insertAdjacentElement('afterend', img);
  return img;
}

/**
 * Mutates one live visual in place to display `logo`. No node is replaced:
 * imgs get a new src/alt (srcset and lazy-load sources cleared so the src
 * wins), background-image elements get a new background (contained), svgs are
 * hidden behind an inserted img. The signature short-circuits steady-state
 * re-applies; a host revert (src/background restored by the site's scripts)
 * is detected and re-applied.
 */
/** What a visual's pass reads before any write of the pass (see `prepareVisual`). */
interface VisualPrep {
  /** Elements the fitted box is written on (see `resolveChain`). */
  chain: Element[];
  /** The visual's current layout box, when it has one. */
  layoutBox: { width: number; height: number } | null;
}

/** Read-only: taken for every visual before the pass writes anything. */
function prepareVisual(visual: Element, cell: Element): VisualPrep {
  const { offsetWidth, offsetHeight } = visual as HTMLElement;
  return {
    chain: resolveChain(visual, cell),
    layoutBox: offsetWidth > 0 && offsetHeight > 0 ? { width: offsetWidth, height: offsetHeight } : null,
  };
}

/**
 * A placement left for the end of the pass: the target's REAL layout box
 * is read once all sizes are written (the site's CSS may cap what we
 * wrote), and read for every target in one go — one layout — before the
 * transforms are written.
 */
interface PendingPlacement {
  target: Element;
  kind: 'img' | 'background';
  inkBox: { width: number; height: number };
  ratio: number;
  fraction: InkFraction;
  own: InkArea;
}

function applyLogoToVisual(
  visual: Element,
  cell: Element,
  prep: VisualPrep,
  logo: VariantLogo,
  height: number | null,
  filterId: string | null,
  containBox: { width: number; height: number } | null = null,
  maxWidth: number | null = null,
  inkFraction: InkFraction | null = null,
  ownBox: { width: number; height: number } | null = null,
): { changed: boolean; placement: PendingPlacement | null } {
  const ratio = logo.width && logo.height ? logo.width / logo.height : null;
  let h = height ? Math.round(height) : null;
  let w = h && ratio ? Math.round(h * ratio) : null;
  // A derived box wider than anything the design ever showed overflows its
  // cell (grid tracks don't grow) — pin the width to the row's widest sampled
  // box and let the height follow the ratio instead (contain math).
  if (w && h && ratio && maxWidth && w > maxWidth) {
    w = Math.round(maxWidth);
    h = Math.round(maxWidth / ratio);
  }
  // With the originals' ink measured, the logo keeps its whole box (the
  // derived one, or the row's shared one — the cell never moves) and its
  // ink is placed at the share of the box the original ink filled (see
  // placeInk), whatever margins its own file carries.
  // Shared-box regime: the box is the visual's OWN original one — a row can
  // give one logo a bigger cell than the others (a featured logo), and the
  // modal box would place its ink in a corner of that cell.
  const inkBox = h && w ? { width: w, height: h } : !h && containBox ? (ownBox ?? containBox) : null;
  const own = logo.ink ?? inkBoxes.get(logo.src) ?? WHOLE_FILE;
  const placing = inkBox && inkFraction && ratio ? { inkBox, fraction: inkFraction, ratio, own } : null;
  const inkKey = placing
    ? `ink${Math.round(placing.inkBox.width)}x${Math.round(placing.inkBox.height)}/${prep.layoutBox ? `${prep.layoutBox.width}x${prep.layoutBox.height}` : '-'}|${placing.fraction.width.toFixed(3)},${placing.fraction.height.toFixed(3)}|${own.left.toFixed(3)},${own.top.toFixed(3)},${own.width.toFixed(3)},${own.height.toFixed(3)}`
    : '';
  const signature = [
    logo.src,
    h ?? '',
    w ?? '',
    filterId ?? '',
    containBox ? `${Math.round(containBox.width)}x${Math.round(containBox.height)}` : '',
    inkKey,
  ].join('|');
  const placementFor = (target: Element, kind: 'img' | 'background'): PendingPlacement | null =>
    placing ? { target, kind, ...placing } : null;

  const dataset = (visual as HTMLElement).dataset;
  const tag = visual.tagName.toLowerCase();
  const none = { changed: false, placement: null };

  if (tag === 'svg') {
    if (dataset[LOGO_SIGNATURE_KEY] === signature && insertedImgFor(visual).src) return none;
    hideInPlace(visual);
    const img = insertedImgFor(visual);
    configureImg(img, logo, filterId);
    // The inserted img stands where the svg stood: same chain, minus the svg.
    const chain = [img, ...prep.chain.filter((el) => el !== visual)];
    let placement: PendingPlacement | null = null;
    if (h && w) {
      sizeChain(chain, cell, h, w);
      placement = placementFor(img, 'img');
    } else if (h) {
      img.style.height = `${h}px`;
      img.style.width = 'auto';
    } else if (inkBox) {
      // Contain-box regime: the inserted img replacing an svg has no box of
      // its own — without one it renders at the file's natural size. Give it
      // the visual's box (the shared one when unknown); object-fit contain
      // letterboxes the logo inside, exactly like the design fits its own
      // assets.
      img.style.width = `${Math.round(inkBox.width)}px`;
      img.style.height = `${Math.round(inkBox.height)}px`;
      placement = placementFor(img, 'img');
    }
    dataset[LOGO_SIGNATURE_KEY] = signature;
    return { changed: true, placement };
  }

  if (isImg(visual)) {
    if (dataset[LOGO_SIGNATURE_KEY] === signature && visual.getAttribute('src') === logo.src) return none;
    configureImg(visual, logo, filterId);
    let placement: PendingPlacement | null = null;
    if (h && w) {
      sizeChain(prep.chain, cell, h, w);
      placement = placementFor(visual, 'img');
    } else if (h) {
      visual.style.height = `${h}px`;
      visual.style.width = 'auto';
    } else if (placing && inkBox) {
      // Pin the img to its original box (the placement math assumes it; a
      // `width: auto` img would otherwise take the injected file's shape).
      visual.style.width = `${Math.round(inkBox.width)}px`;
      visual.style.height = `${Math.round(inkBox.height)}px`;
      placement = placementFor(visual, 'img');
    }
    dataset[LOGO_SIGNATURE_KEY] = signature;
    return { changed: true, placement };
  }

  // Background-image element.
  const current = (visual as HTMLElement).style.backgroundImage;
  if (dataset[LOGO_SIGNATURE_KEY] === signature && current.includes(logo.src)) return none;
  const style = (visual as HTMLElement).style;
  style.backgroundImage = `url("${logo.src}")`;
  style.backgroundSize = 'contain';
  style.backgroundRepeat = 'no-repeat';
  style.backgroundPosition = 'center';
  if (filterId) style.filter = `url(#${filterId})`;
  if (h && w) sizeChain(prep.chain, cell, h, w);
  else if (h) style.height = `${h}px`;
  dataset[LOGO_SIGNATURE_KEY] = signature;
  return { changed: true, placement: placementFor(visual, 'background') };
}

/**
 * Writes the deferred placements: every target's real box is read first
 * (one layout for the whole row), then every transform is written.
 */
function writePlacements(placements: PendingPlacement[]): void {
  const boxes = placements.map((p) => layoutBoxOf(p.target, p.inkBox));
  placements.forEach((p, i) => {
    const box = boxes[i];
    const placed = placeInk(p.ratio, box, p.fraction, p.own);
    const fit = {
      scale: Number(placed.scale.toFixed(3)),
      tx: Number(placed.tx.toFixed(2)),
      ty: Number(placed.ty.toFixed(2)),
    };
    if (p.kind === 'img') {
      placeInkInBox(p.target as HTMLImageElement, fit);
      return;
    }
    // Same placement expressed as a background: the file drawn at its
    // scaled size, positioned so the ink sits at the centre.
    const style = (p.target as HTMLElement).style;
    const drawnWidth = placed.fileWidth * fit.scale;
    const drawnHeight = placed.fileHeight * fit.scale;
    style.backgroundSize = `${Math.round(drawnWidth)}px ${Math.round(drawnHeight)}px`;
    style.backgroundPosition = `${Math.round((box.width - drawnWidth) / 2 + fit.tx)}px ${Math.round((box.height - drawnHeight) / 2 + fit.ty)}px`;
  });
}

/**
 * Places an img's ink inside its own box (see placeInk): the file is
 * contained in the box, then scaled and shifted about the box centre. A
 * transform never moves the layout, so the cell keeps its size.
 */
function placeInkInBox(
  img: HTMLImageElement,
  fit: { scale: number; tx: number; ty: number },
): void {
  img.style.transformOrigin = '50% 50%';
  img.style.transform = `translate(${fit.tx}px, ${fit.ty}px) scale(${fit.scale})`;
}

/** Points an img at the logo, clearing competing sources. */
function configureImg(img: HTMLImageElement, logo: VariantLogo, filterId: string | null): void {
  img.setAttribute('src', logo.src);
  img.setAttribute('alt', logo.alt ?? '');
  img.removeAttribute('srcset');
  img.removeAttribute('sizes');
  img.removeAttribute('data-src');
  img.removeAttribute('data-srcset');
  img.style.objectFit = 'contain';
  if (filterId) img.style.filter = `url(#${filterId})`;
}

/**
 * Applies a `customer_logo` template to a live element IN PLACE. The template
 * is only a guide (slot count, declared heights); the site's own DOM nodes
 * are mutated, never replaced, so animations, listeners, and framework state
 * keep working. Live visuals are numbered by identity (see `assignSlots`):
 * the first distinct logo gets the first company, and every clone of it —
 * marquee tracks before and after the originals — gets that same company,
 * whatever the live clone count. Visuals whose slot has no company hide
 * their whole cell (in every clone, so a loop stays coherent); an empty
 * companies list leaves the target element untouched (the page keeps its own
 * logos).
 */
export interface ApplyLogoTemplateOptions {
  /**
   * Called when an asynchronous measurement of the row's originals settles
   * (their ink boxes) and a fresh pass would size the logos better.
   */
  onRefine?: () => void;
  /**
   * Which row of a grouped template this element is (its position among
   * the selector's matches, see `logosForCarousel`). Default: the first.
   */
  carouselIndex?: number;
}

export function applyLogoTemplateInPlace(
  element: Element,
  template: LogoTemplate,
  logos: VariantLogo[],
  fillColor: string | null,
  options: ApplyLogoTemplateOptions = {},
): LogoTemplateApplyResult {
  const base: LogoTemplateApplyResult = {
    liveVisuals: 0,
    liveUniqueVisuals: 0,
    pairing: 'no-visuals',
    strategy: 'none',
    calibration: [],
    heights: [],
    applied: 0,
    hiddenCells: 0,
    layoutChanged: false,
    inkFraction: null,
  };

  // Nothing to show: leave the page's own logos untouched (never hide).
  if (logos.length === 0) return { ...base, pairing: 'empty' };

  const visuals = findLogoVisuals(element);
  base.liveVisuals = visuals.length;
  if (visuals.length === 0) return base;

  // Nothing is measured (nor persisted) on an unstyled document: wait for
  // the stylesheets, then let the caller apply again.
  const { onRefine } = options;
  const waitFor = (targets: Element[]) => {
    if (!onRefine) return;
    targets.forEach((target) => {
      if (watchedLinks.has(target)) return;
      watchedLinks.add(target);
      target.addEventListener('load', () => onRefine(), { once: true });
      target.addEventListener('error', () => onRefine(), { once: true });
    });
  };
  const pendingLinks = pendingStylesheets(element.ownerDocument);
  if (pendingLinks.length > 0) {
    waitFor(pendingLinks);
    return { ...base, pairing: 'pending-styles' };
  }
  // The originals' boxes are read before the swap; a `width: auto` img has
  // none until its file has loaded (modjo's marquee measured nothing and
  // kept an empty calibration for good). Wait for the row's images while
  // the document loads — after `load`, only lazy images are still pending,
  // and those must not hold the row back.
  const pendingImages =
    element.ownerDocument.readyState === 'complete'
      ? []
      : visuals.filter((v) => isImg(v) && !v.complete);
  if (pendingImages.length > 0) {
    waitFor(pendingImages);
    return { ...base, pairing: 'pending-images' };
  }

  // Identity is read (and persisted) BEFORE any mutation: the sources are
  // about to be rewritten.
  const carousel = template.carousels[options.carouselIndex ?? 0] ?? template.carousels[0];
  const { slotOf, uniqueCount } = assignSlots(visuals, template, carousel);
  base.liveUniqueVisuals = uniqueCount;
  base.pairing =
    uniqueCount < visuals.length
      ? 'clones'
      : visuals.length === (carousel?.slotCount ?? template.uniqueSlotCount)
        ? 'one-to-one'
        : 'mismatch';
  const cells = visuals.map((v) => findVisualCell(v, element));
  // Each visual's own original box, read before any mutation (persisted).
  const ownBoxes = visuals.map((v, i) => ownBoxOf(v, cells[i]));
  // Every other read of the pass, for every visual, before any write:
  // reads interleaved with writes force a full layout each (seconds on a
  // long page).
  const preps = visuals.map((v, i) => prepareVisual(v, cells[i]));

  // The calibration curve is learned once per element, BEFORE any mutation,
  // and persisted on the element: a re-apply (host revert, observer pass)
  // must not re-learn the curve from the boxes we resized ourselves. A
  // hydration wipe that replaces the element drops the marker along with our
  // sizing, so the fresh original content is re-learned correctly.
  const host = element as HTMLElement;
  let calibration: CalibrationPair[] | null = null;
  const stored = host.dataset?.tinctLogoCalibration;
  if (stored) {
    try {
      calibration = JSON.parse(stored) as CalibrationPair[];
    } catch {
      calibration = null;
    }
  }
  // A stored empty curve is no curve (persisted by an earlier pass that
  // could not measure): learn again.
  if (!calibration || calibration.length === 0) {
    calibration = rowCalibration(visuals, cells);
    // Only a learned curve is worth keeping; nothing measured means the
    // next pass must try again (the boxes may simply not exist yet).
    if (host.dataset && calibration.length > 0) {
      host.dataset.tinctLogoCalibration = JSON.stringify(calibration);
    }
  }
  base.calibration = calibration;

  // One shared box for every logo (deepneuronic-style) means the design fits
  // each asset INSIDE the box: swap sources and let contain do the sizing —
  // resizing boxes to the injected ratios would read oversized.
  const containBox = modalRowBox(calibration);

  // How much of their boxes the ORIGINAL logos' ink filled. Their sources
  // and boxes are read before the swap (first pass) and kept for later
  // passes; the measurement itself is asynchronous and persisted on the
  // host once known, so a re-apply (or a fresh SDK on the same document)
  // never waits for it twice.
  let inkFraction: InkFraction | null = null;
  const storedInk = host.dataset?.tinctLogoInk;
  if (storedInk) {
    try {
      inkFraction = JSON.parse(storedInk) as InkFraction;
    } catch {
      inkFraction = null;
    }
  }
  if (!inkFraction) {
    let originals = rowOriginals.get(element);
    if (!originals) {
      const untouched = visuals.every(
        (v) => !(v as HTMLElement).dataset?.[LOGO_SIGNATURE_KEY],
      );
      if (untouched) {
        originals = visuals.flatMap((visual, i) => {
          const src = originalSourceOf(visual);
          const box = declaredVisualSize(visual, cells[i]);
          return src && box ? [{ src, box }] : [];
        });
        rowOriginals.set(element, originals);
      }
    }
    if (originals && originals.length > 0) {
      requestInkBoxes(
        originals.map((o) => o.src),
        options.onRefine,
      );
      if (originals.every((o) => inkBoxes.has(o.src))) {
        inkFraction = inkFractionFor(
          originals.map((o) => ({ ink: inkBoxes.get(o.src), box: o.box })),
        );
        if (inkFraction && host.dataset) host.dataset.tinctLogoInk = JSON.stringify(inkFraction);
      }
    }
  }
  base.inkFraction = inkFraction;
  // The injected files' own ink, measured once each: a file with margins
  // around its mark must not be placed by its file box.
  if (inkFraction) {
    requestInkBoxes(
      logos.filter((logo) => !logo.ink).map((logo) => logo.src),
      options.onRefine,
    );
  }

  const calibrationHeights = calibration.map((p) => p.height);
  base.strategy = containBox
    ? 'contain-box'
    : calibration.length === 0
      ? fallbackHeight(calibration, template) !== null
        ? 'fallback'
        : 'none'
      : calibration.length >= 2 &&
          Math.max(...calibrationHeights) - Math.min(...calibrationHeights) <=
            UNIFORM_HEIGHT_TOLERANCE_PX
        ? 'uniform'
        : calibration.length < 3
          ? 'constant-weight'
          : 'interpolated';

  const filterId = fillColor ? ensureFillFilter(element.ownerDocument, fillColor) : null;

  // Widest box the original row ever gave a logo: the cap for derived widths.
  const rowMaxWidth =
    calibration.reduce((max, p) => (p.width ? Math.max(max, p.width) : max), 0) || null;

  // One height per distinct slot: clones of a logo share its box.
  const heightOfSlot = (slot: number): number | null => {
    const logo = logos[slot];
    if (!logo || containBox) return null;
    const ratio = logo.width && logo.height ? logo.width / logo.height : null;
    const derived = ratio ? heightForRatio(calibration, ratio) : null;
    const h = derived ?? fallbackHeight(calibration, template);
    return h ? Math.round(h) : null;
  };
  const heights = Array.from({ length: uniqueCount }, (_s, slot) => heightOfSlot(slot));
  base.heights = heights;

  const placements: PendingPlacement[] = [];
  visuals.forEach((visual, i) => {
    const slot = slotOf[i];
    const logo = logos[slot];
    if (!logo) {
      if (hideCell(cells[i])) base.layoutChanged = true;
      base.hiddenCells++;
      return;
    }
    // A cell hidden by an earlier pass (no company then) shows again.
    if (showCell(cells[i])) base.layoutChanged = true;
    const { changed, placement } = applyLogoToVisual(
      visual,
      cells[i],
      preps[i],
      logo,
      heights[slot],
      filterId,
      containBox,
      rowMaxWidth,
      inkFraction,
      ownBoxes[i],
    );
    if (changed) base.layoutChanged = true;
    if (placement) placements.push(placement);
    base.applied++;
  });
  // Sizes are written: one layout to read every real box, then the transforms.
  if (placements.length > 0) writePlacements(placements);

  if (base.layoutChanged) notifyLayoutChange(element);

  return base;
}
